import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import KitCard from "@/pages/admin/kit/components/KitCard";
import KitFormModal from "@/pages/admin/kit/components/KitFormModal";
import { DEFAULT_KIT_ITEMS } from "@/utils/kitDefaults";
import { formatNumber } from "@/utils/format";
import type { KitItem } from "@/types";

const KitAdminPage = () => {
  const { data, updateSettings } = useApp();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<KitItem | null>(null);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  // Si aún no se ha personalizado nada, se parte del contenido por defecto.
  const items = useMemo<KitItem[]>(
    () => (data.settings.kitItems.length ? data.settings.kitItems : DEFAULT_KIT_ITEMS),
    [data.settings.kitItems]
  );

  const flash = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(""), 2600);
  };

  const persist = async (next: KitItem[], msg: string) => {
    setBusy(true);
    await updateSettings({ kitItems: next });
    setBusy(false);
    flash(msg);
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (item: KitItem) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleSubmit = async (item: KitItem) => {
    const exists = items.some((i) => i.id === item.id);
    const next = exists ? items.map((i) => (i.id === item.id ? item : i)) : [...items, item];
    setFormOpen(false);
    await persist(next, exists ? "Pieza actualizada." : "Pieza publicada en el kit.");
  };

  const handleDelete = async (item: KitItem) => {
    await persist(
      items.filter((i) => i.id !== item.id),
      "Pieza eliminada del kit."
    );
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Kit de Ventas & Marketing"
        subtitle="Aquí creas y editas el material que tus asesores comparten por WhatsApp y redes. Todo lo que publiques aparece al instante en la app de cada asesor."
      />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
        {[
          { icon: "ri-image-2-line", label: "Piezas en el kit", value: formatNumber(items.length), bubble: "bg-primary-500/25 text-primary-100" },
          { icon: "ri-file-copy-2-line", label: "Copias listas", value: formatNumber(items.length), bubble: "bg-accent-500/20 text-accent-200" },
          { icon: "ri-share-forward-line", label: "Compartir por WhatsApp", value: "1 clic", bubble: "bg-secondary-500/25 text-secondary-200" },
          { icon: "ri-team-line", label: "Asesores que lo ven", value: formatNumber(data.distributors.length), bubble: "bg-accent-500/15 text-accent-100" },
        ].map((s) => (
          <div key={s.label} className="glass-panel rounded-lg p-4 animate-fade-up">
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.bubble}`}>
              <i className={`${s.icon} text-lg`} />
            </span>
            <p className="text-[11px] text-foreground-500 mt-3">{s.label}</p>
            <p className="text-lg md:text-xl font-heading font-extrabold text-foreground-950 mt-0.5 break-words">
              {s.value}
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5 mb-4">
        <p className="text-xs text-foreground-600">
          Agrega las piezas que quieras: cada una lleva imagen, título y texto para compartir.
        </p>
        <button
          onClick={openCreate}
          disabled={busy}
          className={`px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 ${
            busy ? "opacity-60" : ""
          }`}
        >
          <i className="ri-add-line" /> Nueva pieza
        </button>
      </div>

      {feedback && (
        <div className="mb-3 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-300" />
          <p className="text-xs text-foreground-800">{feedback}</p>
        </div>
      )}

      {items.length === 0 ? (
        <div className="glass-panel rounded-lg px-5 py-12 text-center">
          <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
            <i className="ri-image-line text-2xl" />
          </span>
          <p className="text-sm text-foreground-700 font-semibold">El kit está vacío</p>
          <p className="text-xs text-foreground-500 mt-1">
            Crea tu primera pieza para que tus asesores tengan material para compartir.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {items.map((item) => (
            <KitCard key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      <KitFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        editing={editing}
      />
    </div>
  );
};

export default KitAdminPage;